import { useCallback, useRef, useState } from 'react';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import {
  canManageBriefTemplateLevel,
  type BriefTemplateDetail,
  type BriefTemplateLevel,
} from '@/entities/brief-template';
import { usePermissions } from '@/entities/user';

/**
 * What the builder may do with a template at its level, behind `brief_template_levels`.
 *
 * Read-only when the caller cannot manage that level. An organization template reaches every
 * workspace, so the first write to one in a visit waits for a confirmation; the write the user
 * asked for then runs as requested. Later writes in the same visit go straight through, because
 * every section action saves on its own and asking each time would bury the editor in dialogs.
 */
export function useBriefTemplateLevelAccess(template: BriefTemplateDetail | undefined) {
  const levelsEnabled = useBooleanFlagValue('brief_template_levels', false);
  const permissions = usePermissions();
  const [pendingWrite, setPendingWrite] = useState<(() => void) | null>(null);
  // Read inside event handlers only, and keyed by id so another template asks again.
  const acknowledgedTemplateId = useRef<string | null>(null);

  // A backend without levels omits `level`. That template is the workspace template it always was,
  // so only the workspace (or legacy) manage right may edit it: a viewer never gets the editor.
  const level: BriefTemplateLevel | undefined = template
    ? ((template.level as BriefTemplateLevel | undefined) ?? 'workspace')
    : undefined;
  const readOnly =
    levelsEnabled && level !== undefined && !canManageBriefTemplateLevel(level, permissions);
  const confirmsWrites = levelsEnabled && level === 'organization' && !readOnly;
  const templateId = template?.id;

  /**
   * Runs `write` straight away unless this is the first real write to an organization template,
   * in which case it waits for the dialog. `changesSomething` lets a Save with nothing to save
   * through without asking, since it sends nothing.
   */
  const withOrganizationConfirm = useCallback(
    <Args extends unknown[]>(
      write: (...args: Args) => void,
      changesSomething: () => boolean = () => true,
    ) =>
      (...args: Args) => {
        if (
          !confirmsWrites ||
          acknowledgedTemplateId.current === templateId ||
          !changesSomething()
        ) {
          write(...args);
          return;
        }
        setPendingWrite(() => () => write(...args));
      },
    [confirmsWrites, templateId],
  );

  /**
   * For a write whose own confirmation modal already carries the organization warning: the
   * user confirming that modal is the acknowledgement, so no second dialog follows.
   */
  const afterOwnConfirmation = useCallback(
    <Args extends unknown[]>(write: (...args: Args) => void) =>
      (...args: Args) => {
        if (confirmsWrites) acknowledgedTemplateId.current = templateId ?? null;
        write(...args);
      },
    [confirmsWrites, templateId],
  );

  const confirm = useCallback(() => {
    acknowledgedTemplateId.current = templateId ?? null;
    const write = pendingWrite;
    setPendingWrite(null);
    write?.();
  }, [pendingWrite, templateId]);

  const cancel = useCallback(() => setPendingWrite(null), []);

  return {
    levelsEnabled,
    level,
    readOnly,
    confirmsWrites,
    withOrganizationConfirm,
    afterOwnConfirmation,
    confirmDialog: { open: pendingWrite !== null, onConfirm: confirm, onCancel: cancel },
  };
}
